import apiClient from "../../../shared/services/apiClient";
import {
  toClinicalEvent,
  type ClinicalEvent,
  type ClinicalEventKind,
  type ClinicalTimelineDensityMode,
  type ClinicalTimelinePayload,
} from "../types/clinicalEvent";

type EegEventRow = {
  eventId: string;
  studyId: string;
  recordingId: string;
  sourceKind: ClinicalEvent["sourceKind"];
  eventTypeCode: string;
  eventLabel: string;
  startTimeSeconds: number;
  endTimeSeconds?: number | null;
  channels?: string[] | null;
  confidenceScore?: number | null;
  severityScore?: number | null;
  notes?: string | null;
  eventStatus?: ClinicalEvent["eventStatus"];
  payloadJson?: Record<string, unknown> | null;
  sourceAnnotationId?: string | null;
  sourceRef?: string | null;
  createdAt?: string;
  updatedAt?: string;
  rowVersion?: number;
};

function mapEventRow(row: EegEventRow): ClinicalEvent & { rowVersion?: number } {
  return {
    ...toClinicalEvent(row),
    rowVersion: row.rowVersion,
  };
}

export async function listClinicalEvents(params: {
  studyId?: string;
  recordingId?: string;
  start?: number;
  end?: number;
  eventType?: string;
  sourceKind?: string;
  status?: ClinicalEvent["eventStatus"];
  limit?: number;
}): Promise<Array<ClinicalEvent & { rowVersion?: number }>> {
  const response = await apiClient.get("/eeg-events", { params });
  const rows = (response.data?.data?.events ||
    response.data?.data?.items ||
    response.data?.data ||
    response.data?.events ||
    response.data?.items ||
    []) as EegEventRow[];
  return (Array.isArray(rows) ? rows : []).map(mapEventRow);
}

/** Active + suppressed candidates for AI triage review queues. */
export async function listTriageClinicalEvents(params: {
  studyId?: string;
  recordingId?: string;
  limit?: number;
}): Promise<Array<ClinicalEvent & { rowVersion?: number }>> {
  const [active, suppressed] = await Promise.all([
    listClinicalEvents({ ...params, status: "active" }),
    listClinicalEvents({ ...params, status: "suppressed" }),
  ]);
  const byId = new Map<string, ClinicalEvent & { rowVersion?: number }>();
  for (const event of [...active, ...suppressed]) {
    byId.set(event.eventId, event);
  }
  return Array.from(byId.values()).sort(
    (a, b) => a.startTimeSeconds - b.startTimeSeconds,
  );
}

export async function fetchEventDensity(params: {
  recordingId: string;
  start: number;
  end: number;
  bucketSec?: number;
}): Promise<{
  buckets: Array<{
    start: number;
    end: number;
    total: number;
    byType: Record<string, number>;
  }>;
}> {
  const response = await apiClient.get("/eeg-events/views/timeline", {
    params,
  });
  return (
    response.data?.data || {
      buckets: [],
    }
  );
}

export async function fetchClinicalTimeline(params: {
  recordingId: string;
  studyId?: string;
  start: number;
  end: number;
  bucketSec?: number;
  kinds?: ClinicalEventKind[];
  densityMode?: ClinicalTimelineDensityMode;
  includeAdapters?: boolean;
}): Promise<ClinicalTimelinePayload> {
  try {
    const response = await apiClient.get(
      "/eeg-events/views/clinical-timeline",
      { params },
    );
    if (response.data?.data) {
      return response.data.data as ClinicalTimelinePayload;
    }
  } catch {
    // Fall back to composing from list + density endpoints.
  }

  const [events, density] = await Promise.all([
    listClinicalEvents({
      recordingId: params.recordingId,
      studyId: params.studyId,
      start: params.start,
      end: params.end,
      limit: 1000,
    }),
    fetchEventDensity({
      recordingId: params.recordingId,
      start: params.start,
      end: params.end,
      bucketSec: params.bucketSec ?? 5,
    }),
  ]);

  const kindFilter = params.kinds?.length
    ? new Set(params.kinds)
    : null;
  const filtered = kindFilter
    ? events.filter((event) => kindFilter.has(event.kind))
    : events;

  return {
    recordingId: params.recordingId,
    start: params.start,
    end: params.end,
    bucketSec: params.bucketSec ?? 5,
    events: filtered,
    buckets: (density.buckets || []).map((bucket) => ({
      ...bucket,
      byKind: {},
    })),
    densityMode: params.densityMode || "normal",
    filters: params.kinds || [],
  };
}

export async function createClinicalEvent(input: {
  studyId: string;
  recordingId: string;
  eventTypeCode: string;
  eventLabel: string;
  startTimeSeconds: number;
  endTimeSeconds?: number;
  channels?: string[];
  notes?: string;
  confidenceScore?: number;
  payloadJson?: Record<string, unknown>;
}): Promise<ClinicalEvent & { rowVersion?: number }> {
  const response = await apiClient.post("/eeg-events", input);
  const row = (response.data?.data || response.data) as EegEventRow;
  return mapEventRow(row);
}

export async function updateClinicalEvent(input: {
  eventId: string;
  rowVersion: number;
  eventStatus?: ClinicalEvent["eventStatus"];
  notes?: string | null;
  payloadJson?: Record<string, unknown>;
  confidenceScore?: number | null;
}): Promise<ClinicalEvent & { rowVersion?: number }> {
  const response = await apiClient.patch(`/eeg-events/${input.eventId}`, {
    rowVersion: input.rowVersion,
    eventStatus: input.eventStatus,
    notes: input.notes,
    payloadJson: input.payloadJson,
    confidenceScore: input.confidenceScore,
  });
  const row = (response.data?.data || response.data) as EegEventRow;
  return mapEventRow(row);
}

/** Hardened detector-run import (S3): provenance fields mandatory. */
export async function importFromDetectorRun(input: {
  runId: string;
  modelCardUri: string;
  modelVersion: string;
  selection: {
    minConfidence: number;
    eventTypes?: string[];
    start?: number;
    end?: number;
    limit?: number;
  };
}): Promise<{
  importedCount: number;
  skippedCount: number;
  sourceRunId: string;
  governanceWarnings?: string[];
}> {
  const response = await apiClient.post(
    "/eeg-events/import-from-detector-run",
    input,
  );
  return (response.data?.data || response.data) as {
    importedCount: number;
    skippedCount: number;
    sourceRunId: string;
    governanceWarnings?: string[];
  };
}

export async function persistArtifactQcDecision(input: {
  studyId: string;
  recordingId: string;
  artifactId: string;
  decision: "accept" | "reject" | "reprocess";
  detectorId?: string;
  channel?: string;
  startSeconds?: number;
  endSeconds?: number;
  reviewerId?: string | null;
  beforeTraceHash?: string;
  afterTraceHash?: string;
}): Promise<ClinicalEvent & { rowVersion?: number }> {
  return createClinicalEvent({
    studyId: input.studyId,
    recordingId: input.recordingId,
    eventTypeCode: "artifact_qc_decision",
    eventLabel: `Artifact QC ${input.decision}`,
    startTimeSeconds: input.startSeconds ?? 0,
    endTimeSeconds: input.endSeconds ?? input.startSeconds ?? 0,
    channels: input.channel ? [input.channel] : undefined,
    notes: `Artifact ${input.artifactId} marked ${input.decision}`,
    confidenceScore: 1,
    payloadJson: {
      kind: "artifact_qc_decision",
      artifactId: input.artifactId,
      decision: input.decision,
      detectorId: input.detectorId || null,
      reviewerId: input.reviewerId || null,
      beforeTraceHash: input.beforeTraceHash || null,
      afterTraceHash: input.afterTraceHash || null,
      decidedAtIso: new Date().toISOString(),
    },
  });
}

/** Persist CX-G02 selective artifact-reduction on/off audit. */
export async function persistArtifactReductionAudit(input: {
  studyId: string;
  recordingId: string;
  action: string;
  reducedTypes: string[];
  showOriginalSignal: boolean;
  actorId?: string | null;
  notes?: string | null;
  modelCardUri?: string;
}): Promise<ClinicalEvent & { rowVersion?: number }> {
  return createClinicalEvent({
    studyId: input.studyId,
    recordingId: input.recordingId,
    eventTypeCode: "artifact_reduction_action",
    eventLabel: `Artifact reduction ${input.action}`,
    startTimeSeconds: 0,
    endTimeSeconds: 0,
    notes: input.notes ?? `Reduction action ${input.action}`,
    confidenceScore: 1,
    payloadJson: {
      kind: "artifact_reduction_action",
      action: input.action,
      reducedTypes: input.reducedTypes,
      showOriginalSignal: input.showOriginalSignal,
      actorId: input.actorId || null,
      modelCardUri:
        input.modelCardUri ||
        "qms://model-card/artifact-reduction-heuristic-v1",
      gapId: "CX-G02",
      assistive: true,
      decidedAtIso: new Date().toISOString(),
    },
  });
}
